import { useViewportScroll } from 'framer-motion';
import { useEffect, useState } from 'preact/hooks';

const initialOptions = {
  marginTop: 0,
};

/**
 * Given a timeline of text strings with enter and exit intervals,
 * this hook returns text for that scroll position such that it
 * looks like the text is being typed and erased.
 *  
 * @param {object} timeline Timeline of events
 * @param {object} options Extra Options
 * @returns {string} Text to be rendered at current scroll position
 */
function useTyper(timeline, { marginTop } = initialOptions) {
  const [text, setText] = useState(timeline.find((time) => time.enterPos[0] == -1)?.text || '');
  const { scrollY } = useViewportScroll();

  useEffect(() => () => scrollY.onChange((v) => {
    v = Math.max(v - marginTop, 0);
    const curTime = timeline.reduce((n, time) => time.enterPos[0] <= v ? time : n, {});

    // Min-max normalization of scroll position to text length
    const endIdx = (interval) => {
      return Math.floor((v - interval[0])/(interval[1] - interval[0]) * curTime.text.length);
    };

    if (curTime.enterPos[0] <= v && curTime.enterPos[1] >= v) {
      // Scroll down inserts more text
      setText(curTime.text.slice(0, 1+endIdx(curTime.enterPos)));

    } else if (curTime.exitPos[0] <= v && curTime.exitPos[1] >= v) {
      // Scroll Up removes some text
      const idx = endIdx(curTime.exitPos);
      setText(idx > 0 ? curTime.text.slice(0, -idx) : curTime.text);

    } else if (text !== curTime.text) {
      // If scroll position has passed the enter or exit intervals, set the complete text accordingly
      setText(curTime.text);
    }
  }), [scrollY, text, setText, timeline, marginTop]);

  return text;
}

export default useTyper;
